import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCampusDetails } from "../../../redux/campusesSlice";
import ViewComplaintReportModal from "./ViewComplaintReportModal";
import toast from "react-hot-toast";

const ComplaintsFeedbacks = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [viewReportOpen, setViewReportOpen] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [viewSectionReport, setViewSectionReport] = useState(false);

  const { campusDetails, loading } = useSelector((state) => state.campuses);

  const selectedCampus = campusDetails.find(
    (campus) => campus.id === selectedCampusId
  ) || { classes: [], teachers: [] };
  
  // Add dummy classes if no classes are available from the campus
  const dummyClasses = [
    { id: "C001", className: "Grade 8" },
    { id: "C002", className: "Grade 9" },
    { id: "C003", className: "Grade 10" },
    { id: "C004", className: "Grade 11" },
    { id: "C005", className: "Grade 12" }
  ];
  
  const availableClasses = selectedCampus.classes && selectedCampus.classes.length > 0 
    ? selectedCampus.classes 
    : dummyClasses;

  const selectedClass = availableClasses.find(
    (cls) => cls.id === selectedClassId
  ) || { sections: [] };
  
  // Add dummy sections if no sections are available from the class
  const dummySections = [
    { id: "S001", sectionName: "Section A" },
    { id: "S002", sectionName: "Section B" },
    { id: "S003", sectionName: "Section C" },
    { id: "S004", sectionName: "Section D" }
  ];
  
  const availableSections = selectedClass.sections && selectedClass.sections.length > 0 
    ? selectedClass.sections 
    : dummySections;

  const selectedSection = availableSections.find(
    (section) => section.id === selectedSectionId
  ) || { teachers: [] };
  
  // Add dummy teachers if no teachers are available from the section
  const dummyTeachers = [
    { id: "T001", fullName: "Dr. Sarah Johnson", teacherId: "TCH001" },
    { id: "T002", fullName: "Mr. Michael Brown", teacherId: "TCH002" },
    { id: "T003", fullName: "Ms. Emily Davis", teacherId: "TCH003" },
    { id: "T004", fullName: "Prof. David Wilson", teacherId: "TCH004" },
    { id: "T005", fullName: "Mr. Johnson", teacherId: "TCH005" }
  ];
  
  const sectionTeachers = selectedSection.teachers && selectedSection.teachers.length > 0 
    ? selectedSection.teachers 
    : dummyTeachers;

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCampusDetails());
    }
  }, [isOpen, dispatch]);

  // Reset dependent fields when campus changes
  useEffect(() => {
    if (selectedCampusId === "") {
      setSelectedRole("");
      setSelectedClassId("");
      setSelectedSectionId("");
      setSelectedTeacherId("");
    }
  }, [selectedCampusId]);

  // Reset dependent fields when role changes
  useEffect(() => {
    if (selectedRole === "") {
      setSelectedClassId("");
      setSelectedSectionId("");
      setSelectedTeacherId("");
    }
  }, [selectedRole]);

  // Reset dependent fields when class changes
  useEffect(() => {
    if (selectedClassId === "") {
      setSelectedSectionId("");
      setSelectedTeacherId("");
    }
  }, [selectedClassId]);

  // Reset teacher ID when section changes
  useEffect(() => {
    if (selectedSectionId === "") {
      setSelectedTeacherId("");
    }
  }, [selectedSectionId]);


  const handleViewSectionReport = () => {
    if (selectedRole === "admin" && selectedCampusId) {
      // dispatch(fetchSectionComplaintReports({ campusId: selectedCampusId }));
      setViewSectionReport(true);
      setViewReportOpen(true);
    } else if (selectedRole === "teacher" && selectedSectionId) {
      //  dispatch(fetchSectionComplaintReports({ sectionId: selectedSectionId, teacherId: selectedTeacherId }));
      setViewSectionReport(true);
      setViewReportOpen(true);
    } else {
      toast.error("Please complete all required selections");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-[60%] relative">
          <h2 className="text-xl font-semibold mb-4">Complaints & Feedbacks</h2>

          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Campus Selection */}
            <div>
              <label htmlFor="campus" className="block mb-2">
                Select Campus
              </label>
              <select
                id="campus"
                className="border p-2 rounded w-full"
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                disabled={loading}
              >
                <option value="" disabled>
                  Choose a campus
                </option>
                {campusDetails.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Role Selection */}
            <div>
              <label htmlFor="role" className="block mb-2">
                Teacher / Admin
              </label>
              <select
                id="role"
                className="border p-2 rounded w-full"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                disabled={!selectedCampusId || loading}
              >
                <option value="" disabled>
                  Select Role
                </option>
                <option value="teacher">Teacher</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          {/* Conditional Fields - Only show for Teacher role */}
          {selectedRole === "teacher" && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              {/* Class Selection */}
              <div>
                <label htmlFor="class" className="block mb-2">
                  Select Class
                </label>
                <select
                  id="class"
                  className="border p-2 rounded w-full"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  disabled={!selectedCampusId || loading}
                >
                  <option value="" disabled>
                    Choose a class
                  </option>
                  {availableClasses.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.className}
                    </option>
                  ))}
                </select>
              </div>

              {/* Section Selection */}
              <div>
                <label htmlFor="section" className="block mb-2">
                  Select Section
                </label>
                <select
                  id="section"
                  className="border p-2 rounded w-full"
                  value={selectedSectionId}
                  onChange={(e) => setSelectedSectionId(e.target.value)}
                  disabled={!selectedClassId || loading}
                >
                  <option value="" disabled>
                    Choose a section
                  </option>
                  {availableSections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.sectionName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Teacher ID Selection - Only show for Teacher role */}
          {selectedRole === "teacher" && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="teacherId" className="block mb-2">
                  Teacher ID
                </label>
                <select
                  id="teacherId"
                  className="border p-2 rounded w-full"
                  value={selectedTeacherId}
                  onChange={(e) => setSelectedTeacherId(e.target.value)}
                  disabled={!selectedSectionId || loading}
                >
                  <option value="" disabled>
                    Choose a teacher
                  </option>
                  {sectionTeachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.fullName} - {teacher.teacherId}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="bg-gray-400 px-4 py-2 rounded text-white"
            >
              Close
            </button>
            <button
              onClick={handleViewSectionReport}
              className="bg-[#241763] px-4 py-2 rounded text-white"
              disabled={
                loading ||
                (selectedRole === "admin" && !selectedCampusId) ||
                (selectedRole === "teacher" &&
                  (!selectedSectionId || !selectedTeacherId))
              }
            >
              View Complaints & Feedbacks Report
            </button>
          </div>
        </div>
      </div>

      <ViewComplaintReportModal
        isOpen={viewReportOpen}
        onClose={() => setViewReportOpen(false)}
        campusId={selectedCampusId}
        teacherId={selectedTeacherId}
        sectionId={selectedSectionId}
        role={selectedRole}
        isSectionReport={viewSectionReport}
        sectionName={selectedSection.sectionName}
        className={selectedClass.className}
        campusName={selectedCampus.name}
      />
    </>
  );
};

export default ComplaintsFeedbacks;
